import { createContext, forwardRef, useContext, useMemo } from 'react';
import { Link as RouterLink, NavLink as RouterNavLink, useLocation } from 'react-router-dom';

export const LANGS = ['es', 'ca'];
export const CA_PREFIX = '/ca';

const LangContext = createContext('es');

export function langFromPath(pathname = '/') {
  return pathname === CA_PREFIX || pathname.startsWith(`${CA_PREFIX}/`) ? 'ca' : 'es';
}

export function stripLang(pathname = '/') {
  const stripped = langFromPath(pathname) === 'ca' ? pathname.slice(CA_PREFIX.length) : pathname;
  return stripped || '/';
}

export function localizePath(path, lang) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return path;
  if (lang !== 'ca' || path === CA_PREFIX || path.startsWith(`${CA_PREFIX}/`) || path.startsWith(`${CA_PREFIX}#`)) return path;
  return path === '/' ? CA_PREFIX : `${CA_PREFIX}${path}`;
}

export function LangProvider({ children }) {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
}

export function useLang() {
  const lang = useContext(LangContext);
  return useMemo(() => ({
    lang,
    // t(castellano, català): devuelve el texto del idioma activo.
    t: (es, ca) => (lang === 'ca' && ca != null ? ca : es),
    path: (to) => localizePath(to, lang),
  }), [lang]);
}

const localize = (to, lang) => {
  if (typeof to === 'string') return localizePath(to, lang);
  if (to && typeof to === 'object' && typeof to.pathname === 'string') return { ...to, pathname: localizePath(to.pathname, lang) };
  return to;
};

export const Link = forwardRef(function Link({ to, ...props }, ref) {
  const lang = useContext(LangContext);
  return <RouterLink ref={ref} to={localize(to, lang)} {...props} />;
});

export const NavLink = forwardRef(function NavLink({ to, ...props }, ref) {
  const lang = useContext(LangContext);
  return <RouterNavLink ref={ref} to={localize(to, lang)} {...props} />;
});
